import {getCapsuleMaxPoints} from '#/lib/scoring'
import {type Slide} from '#/types/capsule'

/**
 * Minimal server capsule-view shape the Hub data layer normalizes into.
 * Field names mirror `app.bsky.feed.defs#capsuleView`.
 */
export type HubCapsule = {
  uri: string
  title: string
  status: 'draft' | 'published'
  themeId?: string
  subject?: string
  level?: string
  coverImage?: string
  playCount?: number
  /** Number of exercise slides — shown as the 🪙 reward on the card art. */
  slideCount?: number
  /**
   * The capsule's video post (its playable surface). CapsulePlay resolves
   * capsules from these posts, so navigation must use the post rkey.
   */
  postUri?: string
  indexedAt: string
  creatorDid: string
  slides: Slide[]
}

export type HubPlay = {
  id: string
  capsuleUri: string
  score: number
  timeMs: number
  status: 'started' | 'completed'
  createdAt: string
}

/** One row shown in a learner rail: the capsule plus the play that put it there. */
export type LearnerItem = {
  play: HubPlay
  capsule: HubCapsule
  /** Rounded score percentage of the maximum achievable points. */
  pct?: number
  maxScore: number
}

export type LearnerSections = {
  /** status 'started' — learner pressed play but did not finish. */
  continueItems: LearnerItem[]
  /** completed with pct < BOOST_THRESHOLD — worth replaying to boost the score. */
  boostItems: LearnerItem[]
  /** everything else completed. */
  historyItems: LearnerItem[]
}

/** Completed plays strictly below this percentage are offered as "replay to boost". */
export const BOOST_THRESHOLD_PCT = 50

export type CreatorSections = {
  drafts: HubCapsule[]
  published: HubCapsule[]
  mostPlayed: HubCapsule[]
}

export function computeScorePct(play: HubPlay, capsule: HubCapsule) {
  const maxScore = getCapsuleMaxPoints(capsule.slides)
  if (maxScore <= 0) return {maxScore, pct: undefined}
  return {
    maxScore,
    pct: Math.round((play.score / maxScore) * 100),
  }
}

/** Keep only the most recent play per capsule. */
export function latestPlayPerCapsule(plays: HubPlay[]): Map<string, HubPlay> {
  const byCapsule = new Map<string, HubPlay>()
  for (const play of plays) {
    const existing = byCapsule.get(play.capsuleUri)
    if (!existing || play.createdAt > existing.createdAt) {
      byCapsule.set(play.capsuleUri, play)
    }
  }
  return byCapsule
}

function toLearnerItem(
  play: HubPlay,
  capsuleByUri: Record<string, HubCapsule>,
): LearnerItem | null {
  const capsule = capsuleByUri[play.capsuleUri]
  if (!capsule) return null
  const {maxScore, pct} = computeScorePct(play, capsule)
  return {play, capsule, pct, maxScore}
}

/**
 * Derives the learner Hub sections from a flat play history.
 * Uses each capsule's latest play: `started` → continue,
 * completed < 50% → replay-to-boost, other completed → history.
 */
export function deriveLearnerSections(
  plays: HubPlay[],
  capsuleByUri: Record<string, HubCapsule>,
): LearnerSections {
  const continueItems: LearnerItem[] = []
  const boostItems: LearnerItem[] = []
  const historyItems: LearnerItem[] = []

  for (const play of latestPlayPerCapsule(plays).values()) {
    const item = toLearnerItem(play, capsuleByUri)
    if (!item) continue
    if (play.status === 'started') {
      continueItems.push(item)
    } else if (
      item.pct !== undefined &&
      item.pct < BOOST_THRESHOLD_PCT
    ) {
      boostItems.push(item)
    } else {
      historyItems.push(item)
    }
  }

  const byNewest = (a: LearnerItem, b: LearnerItem) =>
    b.play.createdAt.localeCompare(a.play.createdAt)
  return {
    continueItems: continueItems.sort(byNewest),
    // Worst scores first — the ones that most need a retake.
    boostItems: boostItems.sort(
      (a, b) => (a.pct ?? 0) - (b.pct ?? 0) || byNewest(a, b),
    ),
    historyItems: historyItems.sort(byNewest),
  }
}

/** Draft rail, published rail, and a "most played" shortlist for creators. */
export function deriveCreatorSections(
  capsules: HubCapsule[],
  mostPlayedLimit = 10,
): CreatorSections {
  const drafts = capsules
    .filter(c => c.status === 'draft')
    .sort((a, b) => b.indexedAt.localeCompare(a.indexedAt))
  const published = capsules
    .filter(c => c.status === 'published')
    .sort((a, b) => b.indexedAt.localeCompare(a.indexedAt))
  const mostPlayed = [...published]
    .filter(c => (c.playCount ?? 0) > 0)
    .sort((a, b) => (b.playCount ?? 0) - (a.playCount ?? 0))
    .slice(0, mostPlayedLimit)
  return {drafts, published, mostPlayed}
}

/**
 * Parent rail: capsule rows representing a child's play activity. For v1 the
 * parent Hub composes the same learner sections per linked child; this helper
 * only flattens them for rendering.
 */
export function deriveParentChildSummary(sections: LearnerSections) {
  return {
    inProgress: sections.continueItems.length,
    toBoost: sections.boostItems.length,
    completed: sections.historyItems.length,
    boostCapsules: sections.boostItems,
  }
}
